if (!customElements.get('mx-cart-recommendations')) {
  const feedback = new Map();
  let instanceCount = 0;
  const elements = () => [...document.querySelectorAll('mx-cart-recommendations')];
  const sourceElement = event => event.target instanceof Element ? event.target.closest('mx-cart-recommendations') : null;
  const cartProducts = cart => [...new Set((cart?.items || []).map(item => String(item.product_id)))];

  customElements.define('mx-cart-recommendations', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.content = this.querySelector('[data-mx-rec-content]');
      this.status = this.querySelector('[data-mx-rec-status]');
      this.headingElement = this.querySelector('[data-mx-rec-title]');
      this.context = this.dataset.context || 'page';
      this.instance = `mx-cart-recs-${this.context}-${++instanceCount}`;
      this.headingElement.id = `${this.instance}-title`;
      this.setAttribute('aria-labelledby', this.headingElement.id);
      this.requestVersion = 0;
      this.drawer = this.closest('cart-drawer');
      const options = {signal: this.controller.signal};
      this.addEventListener('change', event => {
        if (event.target.matches('[data-mx-rec-select]')) this.syncSelection(event.target);
      }, options);
      this.addEventListener('submit', () => {
        feedback.delete(this.context);
        this.renderFeedback();
      }, {...options, capture: true});
      if (this.drawer) {
        this.drawer.addEventListener('dialog:before-show', () => this.load(), options);
        this.drawerObserver = new MutationObserver(() => {
          if (this.drawer.hasAttribute('open')) this.load();
        });
        this.drawerObserver.observe(this.drawer, {attributes: true, attributeFilter: ['open']});
      } else if ('IntersectionObserver' in window && !window.Shopify?.designMode) {
        this.visibilityObserver = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            this.visible = true;
            this.visibilityObserver.disconnect();
            this.load();
          }
        }, {rootMargin: '200px'});
        this.visibilityObserver.observe(this.closest('.shopify-section') || this.parentElement);
      } else {
        this.visible = true;
      }
      this.renderFeedback();
      if (this.isReady()) this.load();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.requestController?.abort();
      this.drawerObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      clearTimeout(this.feedbackTimer);
      this.pendingKey = null;
      this.controller = null;
    }

    isReady() {
      return this.drawer ? this.drawer.hasAttribute('open') : Boolean(this.visible);
    }

    updateCart(cart) {
      this.dataset.cartProductIds = cartProducts(cart).join(',');
      this.dataset.seed = cart?.items?.[0]?.product_id || '';
      this.invalidate();
    }

    invalidate() {
      this.requestVersion += 1;
      this.requestController?.abort();
      this.pendingKey = null;
      this.loadedKey = null;
      const inCart = new Set((this.dataset.cartProductIds || '').split(','));
      this.content.querySelectorAll('[data-mx-rec-card]').forEach(card => {
        if (inCart.has(card.dataset.productId)) card.remove();
      });
      this.renderFeedback();
      if (this.isReady()) this.load();
    }

    url(related) {
      const url = new URL(related ? this.dataset.recommendationsUrl : this.dataset.cartUrl, location.origin);
      url.searchParams.set('section_id', 'mx-cart-recommendations');
      if (related) {
        url.searchParams.set('product_id', this.dataset.seed);
        url.searchParams.set('limit', '10');
        url.searchParams.set('intent', 'complementary');
      }
      const previewTheme = new URL(location.href).searchParams.get('preview_theme_id');
      if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
      return url;
    }

    async readResult(url, signal) {
      const response = await fetch(url, {signal, credentials: 'same-origin', headers: {'X-Requested-With': 'XMLHttpRequest'}});
      if (!response.ok) throw new Error('Recommendations unavailable');
      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const result = documentFragment.querySelector('[data-mx-cart-recommendations-result]');
      if (!result) throw new Error('Recommendations missing');
      return result;
    }

    async load() {
      if (!this.isConnected || !this.content) return;
      const key = `${this.dataset.seed || ''}:${this.dataset.cartProductIds || ''}`;
      if (this.loadedKey === key || this.pendingKey === key) return;
      this.requestController?.abort();
      this.requestController = new AbortController();
      const signal = this.requestController.signal;
      const version = ++this.requestVersion;
      this.pendingKey = key;
      this.setAttribute('aria-busy', 'true');
      try {
        let result;
        if (this.dataset.seed) {
          try {
            result = await this.readResult(this.url(true), signal);
          } catch (error) {
            if (error.name === 'AbortError') throw error;
            result = await this.readResult(this.url(false), signal);
          }
        } else {
          result = await this.readResult(this.url(false), signal);
        }
        if (signal.aborted || !this.isConnected || version !== this.requestVersion) return;
        const inCart = new Set((this.dataset.cartProductIds || '').split(','));
        result.querySelectorAll('[data-mx-rec-card]').forEach(card => {
          if (inCart.has(card.dataset.productId)) card.remove();
        });
        const fragment = document.createDocumentFragment();
        [...result.childNodes].forEach(node => fragment.append(document.importNode(node, true)));
        this.uniqueIds(fragment);
        this.content.replaceChildren(fragment);
        this.content.querySelectorAll('[data-mx-rec-select]').forEach(select => this.syncSelection(select));
        this.loadedKey = key;
        this.renderFeedback();
      } catch (error) {
        if (error.name !== 'AbortError' && version === this.requestVersion) this.renderFeedback();
      } finally {
        if (version === this.requestVersion) {
          this.pendingKey = null;
          this.removeAttribute('aria-busy');
        }
      }
    }

    uniqueIds(fragment) {
      const ids = new Map();
      fragment.querySelectorAll('[id]').forEach(element => {
        const oldId = element.getAttribute('id');
        const newId = `${this.instance}-${oldId}`;
        ids.set(oldId, newId);
        element.setAttribute('id', newId);
      });
      ['for', 'form', 'aria-labelledby', 'aria-describedby', 'aria-controls'].forEach(attribute => {
        fragment.querySelectorAll(`[${attribute}]`).forEach(element => {
          element.setAttribute(attribute, element.getAttribute(attribute).split(/\s+/).map(id => ids.get(id) || id).join(' '));
        });
      });
    }

    syncSelection(select) {
      const card = select.closest('[data-mx-rec-card]');
      const option = select.selectedOptions[0];
      if (!card || !option) return;
      const form = select.closest('form');
      const input = form?.querySelector('input[name="id"]');
      if (input) input.value = option.value;
      const sizeLabel = option.textContent.trim().replace(/\s+/g, ' ');
      card.querySelector('[data-mx-rec-selection]').textContent = sizeLabel.replace(/\b(?:women|woman|womens|women’s|women's)\b/gi, 'W').replace(/\b(?:men|mens|men’s|men's)\b/gi, 'M').replace(/\s*\/\s*/g, ' / ');
      select.title = sizeLabel;
      card.querySelector('[data-mx-rec-price]').textContent = option.dataset.price || '';
      const compare = card.querySelector('[data-mx-rec-compare]');
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
      if (option.dataset.url) card.querySelectorAll('[data-mx-rec-product-link]').forEach(link => {link.href = option.dataset.url;});
      const image = card.querySelector('.mx-cart-rec__image');
      if (image && option.dataset.image) {
        image.src = option.dataset.image;
        image.srcset = option.dataset.srcset || '';
      }
    }

    setFeedback(message, error = false) {
      feedback.set(this.context, {message, error, expires: Date.now() + 45000});
      elements().filter(element => element.dataset.context === this.context).forEach(element => element.renderFeedback());
    }

    renderFeedback() {
      clearTimeout(this.feedbackTimer);
      const state = feedback.get(this.context);
      if (state && state.expires <= Date.now()) feedback.delete(this.context);
      const active = feedback.get(this.context);
      this.status.textContent = active?.message || '';
      this.status.hidden = !active?.message;
      this.status.classList.toggle('mx-cart-rec__status--error', Boolean(active?.error));
      this.status.setAttribute('role', active?.error ? 'alert' : 'status');
      this.status.setAttribute('aria-live', active?.error ? 'assertive' : 'polite');
      this.headingElement.textContent = this.dataset.seed ? this.dataset.title : this.dataset.emptyTitle;
      const hasProducts = Boolean(this.content.querySelector('[data-mx-rec-card]'));
      this.headingElement.hidden = !hasProducts;
      this.hidden = !hasProducts && !active?.message;
      if (active) this.feedbackTimer = setTimeout(() => {if (this.isConnected) this.renderFeedback();}, active.expires - Date.now() + 1);
    }
  });

  document.addEventListener('cart:change', event => {
    if (event.detail?.cart) elements().forEach(element => element.updateCart(event.detail.cart));
  });
  document.addEventListener('cart:refresh', () => elements().forEach(element => element.invalidate()));
  document.addEventListener('cart-drawer:refreshed', event => {
    event.target.querySelectorAll('mx-cart-recommendations').forEach(element => element.invalidate());
  });
  document.addEventListener('cart:error', event => {
    const source = sourceElement(event);
    if (source) source.setFeedback(event.detail?.error || event.detail?.message || source.dataset.errorMessage, true);
  });
  document.addEventListener('variant:add', event => {
    const source = sourceElement(event);
    if (source) source.setFeedback(source.dataset.addedMessage);
  });
  document.addEventListener('shopify:section:load', event => {
    event.target.querySelectorAll('mx-cart-recommendations').forEach(element => element.invalidate());
  });
}
